/**
 * Catálogo de estilos para la capacidad "Reestilizar 360".
 *
 * Espejo (resumido) de `docs/prompts/estilos_360.json` — acá sólo van label +
 * descripción corta para el selector visual. Los prompts canónicos viven en el
 * agente `xvision-360` (system prompt), que es quien arma la generación. El
 * frontend NO manda prompts: manda una orden en lenguaje natural y el agente
 * resuelve (preset conocido o estilo nuevo sintetizado).
 *
 * Estilos nuevos que el operador crea desde la UI se guardan en localStorage y
 * aparecen en la lista la próxima vez.
 */

export interface Estilo360 {
  key: string;
  label: string;
  desc: string;
  /** true = lo agregó el operador desde la UI (no es preset oficial). */
  custom?: boolean;
}

export const ESTILOS_PRESET: Estilo360[] = [
  { key: 'oleo_clasico', label: 'Óleo clásico', desc: 'Pintura al óleo, pinceladas, paleta terrosa' },
  { key: 'realismo_magico', label: 'Realismo mágico', desc: 'Niebla cálida, luz miel, atmósfera onírica' },
  { key: 'hora_azul', label: 'Hora azul', desc: 'Atardecer cobalto, luces ámbar, cinemático' },
  { key: 'pixar_3d', label: 'Pixar 3D', desc: 'Animación 3D cálida y redondeada' },
  { key: 'spider_verse', label: 'Spider-Verse', desc: 'Cómic, tramas de puntos, pop alto contraste' },
  { key: 'ghibli_anime', label: 'Ghibli', desc: 'Fondo anime pintado a mano, cielos amplios' },
  { key: 'storybook', label: 'Storybook', desc: 'Ilustración de cuento, colores saturados' },
  { key: 'holografico_neon', label: 'Holográfico neón', desc: 'Wireframes cian/magenta, HUD sci-fi' },
  { key: 'cyberpunk_noche', label: 'Cyberpunk', desc: 'Noche Blade Runner, neón, vapor, reflejos' },
  { key: 'fantasy_epico', label: 'Fantasy épico', desc: 'Rayos de luz, niebla, color cinematográfico' },
  { key: 'cristal_surreal', label: 'Cristal surreal', desc: 'Vidrio translúcido, refracciones, Dalí' },
  { key: 'gemelo_cian', label: 'Gemelo digital · cian', desc: 'Wireframe data-viz cian, nodos de datos' },
  { key: 'gemelo_blanco', label: 'Gemelo digital · blanco', desc: 'Wireframe blanco, camino iluminado' },
  { key: 'gemelo_dorado', label: 'Gemelo digital · dorado', desc: 'Wireframe dorado premium, ruta luminosa' },
  { key: 'matrix', label: 'Matrix', desc: 'Lluvia de código verde, fósforo, scanlines' },
];

const LS_KEY = 'xvision.estilos360.custom';

export function getEstilosCustom(): Estilo360[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as Estilo360[];
    return Array.isArray(parsed) ? parsed.map((e) => ({ ...e, custom: true })) : [];
  } catch {
    return [];
  }
}

export function saveEstiloCustom(label: string, desc: string): Estilo360 {
  const key = 'custom_' + label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
  const nuevo: Estilo360 = { key, label: label.trim(), desc: desc.trim(), custom: true };
  const actuales = getEstilosCustom().filter((e) => e.key !== key);
  try {
    localStorage.setItem(LS_KEY, JSON.stringify([...actuales, nuevo]));
  } catch {
    /* localStorage lleno o no disponible — el estilo igual se usa este turno */
  }
  return nuevo;
}

export function getEstilos360(): Estilo360[] {
  return [...ESTILOS_PRESET, ...getEstilosCustom()];
}

/**
 * Arma la orden en lenguaje natural que se manda al chat. El agente xvision-360
 * la interpreta: si el estilo es preset usa su receta; si es custom lo sintetiza.
 */
export function construirOrden(opts: {
  estilo: Estilo360;
  animar: boolean;
  upscale: boolean;
}): string {
  const { estilo, animar, upscale } = opts;
  const base = estilo.custom
    ? `Reestilizá esta 360 a un estilo nuevo: "${estilo.label}" — ${estilo.desc}.`
    : `Reestilizá esta 360 al estilo "${estilo.label}".`;
  const extras: string[] = [];
  if (animar) extras.push('animala a video (cámara fija, sólo movimiento ambiental del estilo)');
  if (upscale) extras.push('subila a 4K');
  const cola = extras.length ? ` Después ${extras.join(' y ')}.` : '';
  const recordatorio = ' Mantené el equirectangular 2:1 intacto.';
  return base + cola + recordatorio;
}
